/**
 * The four field types that reach into another table.
 *
 * A link row points at a table. A count, a rollup and a lookup reach *through*
 * one of those links: the link says which table, and the field on the other side
 * says which column of it. So all three share two pickers, and the second one
 * cannot be filled in until the first has been.
 *
 * **The formatting comes from the backend, as it does for a formula.** A rollup
 * field's `save()` builds `sum(lookup('Through','Target'))` and stores it as the
 * field's formula; a lookup's builds the same without the function. Asking the
 * typing endpoint about that exact string is asking what the field will be, and
 * the answer carries the format that type is shown in -- so nothing here knows
 * that averaging dates gives a date and counting them gives a number.
 */
import { useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { Field } from '@/api/types'
import { useApplications } from '@/features/application/queries'
import { NativeSelect, NativeSelectOption } from '@/shared/ui/native-select'
import { useFields, useViews } from '../../../table/queries'
import { typeFormula, type FormulaTyping } from '../../../formula/type-formula'
import { FORMULA_TYPE_BEHAVES_AS, isArrayFormulaType } from '../../formula-types'
import {
  ROLLUP_FUNCTIONS,
  linkRowFields,
  linkedTableId,
  relationExpression,
  tablesBeside,
} from '../../relations'
import { Setting, Toggle } from './controls'
import {
  DateFormatting,
  DurationFormatting,
  NumberFormatting,
  changing,
  type DateFormattingProperties,
  type DurationFormattingProperties,
  type NumberFormattingProperties,
} from './formatting'
import type { SubForm, SubFormProps } from './types'

// ---------------------------------------------------------------------------
// Link row
// ---------------------------------------------------------------------------

export const LinkRowForm: SubForm<'link_row'> = ({ properties, onChange, field }) => {
  const { t } = useTranslation()
  const table = useId()
  const related = useId()
  const multiple = useId()
  const applications = useApplications(field.workspace_id)
  const tables = tablesBeside(applications.data, field.table_id)

  const linked = properties.link_row_table_id ?? null
  // A link row pointing at its own table cannot have a field on the other side:
  // the other side is this side, and the backend refuses the combination.
  const itself = linked === field.table_id
  const limited = properties.link_row_limit_selection_view_id ?? null

  return (
    <>
      <Setting id={table} label={t('fieldConfig.linkedTable')}>
        <NativeSelect
          id={table}
          className="w-full"
          value={linked === null ? '' : String(linked)}
          onChange={(event) => {
            const link_row_table_id =
              event.target.value === '' ? null : Number(event.target.value)
            onChange({
              link_row_table_id,
              // The view that limits which rows can be picked belongs to the
              // table being pointed at. Left behind when the table changes it
              // would name a view of somewhere else, which the backend refuses
              // with `ViewNotInTable`.
              link_row_limit_selection_view_id: null,
              ...(link_row_table_id === field.table_id
                ? { has_related_field: false }
                : {}),
            })
          }}
        >
          <NativeSelectOption value="">
            {t('fieldConfig.linkedTableNone')}
          </NativeSelectOption>
          {tables.map((table) => (
            <NativeSelectOption key={table.id} value={table.id}>
              {table.name}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </Setting>

      {!itself && (
        <Toggle
          id={related}
          label={t('fieldConfig.relatedField')}
          hint={t('fieldConfig.relatedFieldHint')}
          checked={properties.has_related_field !== false}
          onChange={(has_related_field) => {
            onChange({ has_related_field })
          }}
        />
      )}

      <Toggle
        id={multiple}
        label={t('fieldConfig.multipleRelationships')}
        hint={t('fieldConfig.multipleRelationshipsHint')}
        checked={properties.link_row_multiple_relationships !== false}
        onChange={(link_row_multiple_relationships) => {
          onChange({ link_row_multiple_relationships })
        }}
      />

      {linked !== null && (
        <LimitToView
          tableId={linked}
          viewId={limited}
          onChange={(link_row_limit_selection_view_id) => {
            onChange({ link_row_limit_selection_view_id })
          }}
        />
      )}
    </>
  )
}

/** Which rows may be picked: all of them, or the ones a view shows. */
function LimitToView({
  tableId,
  viewId,
  onChange,
}: {
  tableId: number
  viewId: number | null
  onChange: (viewId: number | null) => void
}) {
  const { t } = useTranslation()
  const limit = useId()
  const chosenView = useId()
  const views = useViews(tableId)
  const listed = views.data ?? []

  return (
    <>
      <Toggle
        id={limit}
        label={t('fieldConfig.limitToView')}
        hint={t('fieldConfig.limitToViewHint')}
        checked={viewId !== null}
        onChange={(on) => {
          onChange(on ? (listed[0]?.id ?? null) : null)
        }}
      />
      {viewId !== null && (
        <Setting id={chosenView} label={t('fieldConfig.view')}>
          <NativeSelect
            id={chosenView}
            className="w-full"
            value={String(viewId)}
            onChange={(event) => {
              onChange(Number(event.target.value))
            }}
          >
            {listed.map((view) => (
              <NativeSelectOption key={view.id} value={view.id}>
                {view.name}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </Setting>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Reaching through a link
// ---------------------------------------------------------------------------

/** The link to reach through, and the field on the other side of it. */
function useRelation(field: Field, through: number | null, target: number | null) {
  const here = useFields(field.table_id)
  const links = linkRowFields(here.data ?? [])
  const throughField = links.find((link) => link.id === through)

  const relatedTable = linkedTableId(throughField)
  // Only asked for once a link has been chosen, which is what `enabled` on the
  // query would otherwise be for: `useFields` is unconditional, so the table id
  // has to be one there is no harm in asking about.
  const there = useFields(relatedTable ?? field.table_id)
  const targets = relatedTable === null ? [] : (there.data ?? [])

  return {
    links,
    throughField,
    targets,
    targetField: targets.find((candidate) => candidate.id === target),
  }
}

function ThroughPicker({
  links,
  chosen,
  onChange,
}: {
  links: readonly Field[]
  chosen: number | null
  onChange: (id: number | null) => void
}) {
  const { t } = useTranslation()
  const id = useId()

  if (links.length === 0) {
    return (
      <p className="text-sm text-destructive">{t('fieldConfig.noLinkToReachThrough')}</p>
    )
  }

  return (
    <Setting id={id} label={t('fieldConfig.throughField')}>
      <NativeSelect
        id={id}
        className="w-full"
        value={chosen === null ? '' : String(chosen)}
        onChange={(event) => {
          onChange(event.target.value === '' ? null : Number(event.target.value))
        }}
      >
        <NativeSelectOption value="">
          {t('fieldConfig.linkedTableNone')}
        </NativeSelectOption>
        {links.map((link) => (
          <NativeSelectOption key={link.id} value={link.id}>
            {link.name}
          </NativeSelectOption>
        ))}
      </NativeSelect>
    </Setting>
  )
}

function TargetPicker({
  targets,
  chosen,
  onChange,
}: {
  targets: readonly Field[]
  chosen: number | null
  onChange: (id: number | null) => void
}) {
  const { t } = useTranslation()
  const id = useId()

  return (
    <Setting id={id} label={t('fieldConfig.targetField')}>
      <NativeSelect
        id={id}
        className="w-full"
        value={chosen === null ? '' : String(chosen)}
        onChange={(event) => {
          onChange(event.target.value === '' ? null : Number(event.target.value))
        }}
      >
        <NativeSelectOption value="">
          {t('fieldConfig.linkedTableNone')}
        </NativeSelectOption>
        {targets.map((candidate) => (
          <NativeSelectOption key={candidate.id} value={candidate.id}>
            {candidate.name}
          </NativeSelectOption>
        ))}
      </NativeSelect>
    </Setting>
  )
}

/**
 * The formatting the field will carry, asked of the backend.
 *
 * Seeded when the *kind* of value changes rather than on every answer, for the
 * same reason the formula editor seeds it: applying the backend's defaults every
 * time would put them back over a format somebody had just chosen.
 */
function RelationFormatting({
  tableId,
  name,
  expression,
  properties,
  onChange,
}: {
  tableId: number
  name: string
  expression: string | null
  properties: DateFormattingProperties &
    DurationFormattingProperties &
    NumberFormattingProperties
  onChange: (changes: Record<string, unknown>) => void
}) {
  const [typing, setTyping] = useState<FormulaTyping | null>(null)
  const seededFor = useRef<string | null>(null)
  // Held so the effect below does not depend on a function the caller rebuilds
  // on every render, which would make it ask again on every render.
  const apply = useRef(onChange)
  apply.current = onChange

  useEffect(() => {
    if (expression === null) {
      setTyping(null)
      return
    }

    let current = true
    void typeFormula(tableId, { formula: expression, name })
      .then((answer) => {
        if (!current) return
        setTyping(answer)
        const now = answer.type
        if (now !== null && now !== seededFor.current) {
          seededFor.current = now
          apply.current(answer.formatting)
        }
      })
      .catch(() => {
        if (current) setTyping(null)
      })

    return () => {
      current = false
    }
  }, [tableId, name, expression])

  const formulaType = typing?.type ?? null
  const entry = formulaType === 'array' ? typing?.arrayType : formulaType
  const kind = isArrayFormulaType(entry) ? FORMULA_TYPE_BEHAVES_AS[entry] : null

  return (
    <>
      {kind === 'number' && (
        <NumberFormatting
          properties={properties}
          onChange={changing<NumberFormattingProperties, Record<string, unknown>>(
            onChange,
          )}
        />
      )}
      {kind === 'date' && (
        <DateFormatting
          properties={properties}
          // Nothing to move: these values are computed on every read rather
          // than stored.
          convertingFrom={undefined}
          onChange={changing<DateFormattingProperties, Record<string, unknown>>(onChange)}
        />
      )}
      {kind === 'duration' && (
        <DurationFormatting
          properties={properties}
          onChange={changing<DurationFormattingProperties, Record<string, unknown>>(
            onChange,
          )}
        />
      )}
    </>
  )
}

/**
 * A count has one picker and no formatting: it counts rows, and that is a number
 * whatever it is counting.
 */
export const CountForm: SubForm<'count'> = ({ properties, onChange, field }) => {
  const { links } = useRelation(field, properties.through_field_id ?? null, null)

  return (
    <ThroughPicker
      links={links}
      chosen={properties.through_field_id ?? null}
      onChange={(through_field_id) => {
        onChange({ through_field_id })
      }}
    />
  )
}

export const LookupForm: SubForm<'lookup'> = (props) => (
  <ReachThrough {...props} rollup={false} />
)

export const RollupForm: SubForm<'rollup'> = ({ properties, ...rest }) => (
  // A rollup carries everything a lookup does and one more property. The shared
  // body is written against the smaller of the two and reads the extra one
  // through a narrow cast of its own, so there is no cast here.
  <ReachThrough properties={properties} {...rest} rollup />
)

/** The body shared by a lookup and a rollup, which differ by one dropdown. */
function ReachThrough({
  properties,
  onChange,
  field,
  tableId,
  name,
  rollup,
}: SubFormProps<'lookup'> & { rollup: boolean }) {
  const through = properties.through_field_id ?? null
  const target = properties.target_field_id ?? null
  const { links, throughField, targets, targetField } = useRelation(
    field,
    through,
    target,
  )
  const rollupFunction = rollup
    ? ((properties as { rollup_function?: string }).rollup_function ?? '')
    : undefined

  return (
    <>
      <ThroughPicker
        links={links}
        chosen={through}
        onChange={(through_field_id) => {
          // The field on the other side belongs to the table the link points
          // at. Left behind when the link changes it would name a column of
          // another table, which the backend refuses.
          onChange({ through_field_id, target_field_id: null })
        }}
      />

      {throughField !== undefined && (
        <TargetPicker
          targets={targets}
          chosen={target}
          onChange={(target_field_id) => {
            onChange({ target_field_id })
          }}
        />
      )}

      {rollup && targetField !== undefined && (
        <RollupFunctionPicker
          chosen={rollupFunction ?? ''}
          onChange={(rollup_function) => {
            onChange({ rollup_function } as never)
          }}
        />
      )}

      {targetField !== undefined && (
        <RelationFormatting
          tableId={tableId}
          name={name}
          expression={relationExpression({
            through: throughField,
            target: targetField,
            ...(rollup ? { rollupFunction: rollupFunction ?? '' } : {}),
          })}
          properties={properties}
          onChange={onChange}
        />
      )}
    </>
  )
}

function RollupFunctionPicker({
  chosen,
  onChange,
}: {
  chosen: string
  onChange: (name: string) => void
}) {
  const { t } = useTranslation()
  const id = useId()

  return (
    <Setting id={id} label={t('fieldConfig.rollupFunction')}>
      <NativeSelect
        id={id}
        className="w-full"
        value={chosen}
        onChange={(event) => {
          onChange(event.target.value)
        }}
      >
        {/* No empty entry. A rollup with no function is refused, in words about
            a function nobody chose, so there is nothing to be gained by
            offering the state that produces them. */}
        {ROLLUP_FUNCTIONS.map((name) => (
          <NativeSelectOption key={name} value={name}>
            {name}
          </NativeSelectOption>
        ))}
      </NativeSelect>
    </Setting>
  )
}
